/**
 * Organisation-timezone date formatting (rules/01 §6, ADR-0003).
 * Every display goes through these; `.toLocale*String()` is lint-banned in feature code.
 * Output is composed from Intl parts with fixed month names so it is identical on every host.
 */
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"] as const;

type DateInput = string | number | Date;

const formatterCache = new Map<string, Intl.DateTimeFormat>();

function partsFormatter(tz: string): Intl.DateTimeFormat {
  let f = formatterCache.get(tz);
  if (!f) {
    f = new Intl.DateTimeFormat("en-US", {
      timeZone: tz,
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
      hourCycle: "h23",
    });
    formatterCache.set(tz, f);
  }
  return f;
}

interface OrgParts {
  year: number;
  month: number;
  day: number;
  hour: number;
  minute: number;
  second: number;
}

function toDate(value: DateInput): Date | null {
  const d = value instanceof Date ? value : new Date(value);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Calendar fields of an instant as seen in `tz`. */
function getOrgParts(value: DateInput, tz: string): OrgParts | null {
  const d = toDate(value);
  if (!d) return null;
  const out: Record<string, number> = {};
  for (const p of partsFormatter(tz).formatToParts(d)) {
    if (p.type !== "literal") out[p.type] = Number(p.value);
  }
  return {
    year: out["year"] ?? 0,
    month: out["month"] ?? 1,
    day: out["day"] ?? 1,
    hour: out["hour"] ?? 0,
    minute: out["minute"] ?? 0,
    second: out["second"] ?? 0,
  };
}

const pad = (n: number) => String(n).padStart(2, "0");

/** "9 Sep 2026" */
export function formatOrgDate(value: DateInput, tz: string): string {
  const p = getOrgParts(value, tz);
  return p ? `${p.day} ${MONTHS[p.month - 1] ?? ""} ${p.year}` : "—";
}

/** "14:02" (24-hour) */
export function formatOrgTime(value: DateInput, tz: string, withSeconds = false): string {
  const p = getOrgParts(value, tz);
  if (!p) return "—";
  return withSeconds ? `${pad(p.hour)}:${pad(p.minute)}:${pad(p.second)}` : `${pad(p.hour)}:${pad(p.minute)}`;
}

/** "9 Sep 2026, 14:02" */
export function formatOrgDateTime(value: DateInput, tz: string): string {
  const p = getOrgParts(value, tz);
  return p ? `${formatOrgDate(value, tz)}, ${pad(p.hour)}:${pad(p.minute)}` : "—";
}

/** "2026-09-09" — the organisation's calendar date for `now`; date inputs default from this, never the browser clock. */
export function getOrgTodayString(tz: string, now: DateInput = new Date()): string {
  const p = getOrgParts(now, tz);
  return p ? `${p.year}-${pad(p.month)}-${pad(p.day)}` : "";
}

/** "2026-09-09" — the organisation calendar date of an instant (seeds date inputs from stored ISO values). */
export function toOrgDateString(value: DateInput, tz: string): string {
  return getOrgTodayString(tz, value);
}

/**
 * "2026-09-09" typed in the organisation's calendar → the ISO instant of that day's start (00:00:00.000) or
 * end (23:59:59.999) in `tz`. Validity windows are stored as instants (ADR-0003); the terminal applies them itself.
 * Two correction passes settle DST transitions; an impossible date (30 Feb) returns null.
 */
export function orgDateToIso(date: string, tz: string, edge: "start" | "end" = "start"): string | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!m) return null;
  const [y, mo, d] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const [h, mi, s, ms] = edge === "start" ? [0, 0, 0, 0] : [23, 59, 59, 999];
  const wall = Date.UTC(y, mo - 1, d, h, mi, s);
  let instant = wall;
  for (let pass = 0; pass < 2; pass += 1) {
    const p = getOrgParts(instant, tz);
    if (!p) return null;
    const seen = Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second);
    instant = wall - (seen - instant);
  }
  const check = getOrgParts(instant, tz);
  if (!check || check.year !== y || check.month !== mo || check.day !== d) return null;
  return new Date(instant + ms).toISOString();
}

/** Relative wording for "last seen" and feeds: no timezone dependence, seconds resolution is deliberately hidden. */
export function formatRelative(value: DateInput, now: DateInput = new Date()): string {
  const d = toDate(value);
  const n = toDate(now);
  if (!d || !n) return "—";
  const diffMs = n.getTime() - d.getTime();
  const sec = Math.round(diffMs / 1000);
  if (sec < 45) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min} min ago`;
  const hours = Math.round(min / 60);
  if (hours < 24) return `${hours} h ago`;
  const days = Math.round(hours / 24);
  if (days < 30) return `${days} d ago`;
  const months = Math.round(days / 30);
  return months < 12 ? `${months} mo ago` : `${Math.round(months / 12)} y ago`;
}

const DAY_MS = 86_400_000;
const DAY_RE = /^\d{4}-\d{2}-\d{2}$/;

/** Pure calendar arithmetic on "YYYY-MM-DD" strings, no zone involved: the day `n` days after `day`. */
export function addDays(day: string, n: number): string {
  const d = new Date(Date.parse(`${day}T00:00:00Z`) + n * DAY_MS);
  return `${d.getUTCFullYear()}-${pad(d.getUTCMonth() + 1)}-${pad(d.getUTCDate())}`;
}

/** A real calendar day: the engine rolls "2026-02-30" over to March, so the round trip has to come back unchanged. */
export function isOrgDayString(value: string): boolean {
  return DAY_RE.test(value) && !Number.isNaN(Date.parse(`${value}T00:00:00Z`)) && addDays(value, 0) === value;
}

/** Whole days from `fromDay` to `toDay` ("2026-09-03" → "2026-09-10" is 7; the same day is 0). */
export function daysBetween(fromDay: string, toDay: string): number {
  return Math.round((Date.parse(`${toDay}T00:00:00Z`) - Date.parse(`${fromDay}T00:00:00Z`)) / DAY_MS);
}

export interface OrgDayRange {
  fromDay: string;
  toDay: string;
}

/** The last `days` organisation days ending today (ADR-0003): "last 7 days" is today and the six before it. */
export function lastOrgDays(days: number, tz: string, now: DateInput = new Date()): OrgDayRange {
  const toDay = getOrgTodayString(tz, now);
  return { fromDay: addDays(toDay, -(days - 1)), toDay };
}

/** An organisation-day range as the ISO instants the API takes: start of the first day, end of the last, in `tz`. */
export function orgDayRangeToIso(range: OrgDayRange, tz: string): { from: string; to: string } | null {
  const from = orgDateToIso(range.fromDay, tz, "start");
  const to = orgDateToIso(range.toDay, tz, "end");
  return from && to ? { from, to } : null;
}

/** "3–10 Sep 2026", "28 Aug – 10 Sep 2026", "10 Sep 2026" — from day strings, no zone involved. */
export function formatDayRange(range: OrgDayRange): string {
  const a = range.fromDay.split("-").map(Number);
  const b = range.toDay.split("-").map(Number);
  const [ay = 0, am = 1, ad = 1] = a;
  const [by = 0, bm = 1, bd = 1] = b;
  const monthA = MONTHS[am - 1] ?? "";
  const monthB = MONTHS[bm - 1] ?? "";
  if (range.fromDay === range.toDay) return `${bd} ${monthB} ${by}`;
  if (ay === by && am === bm) return `${ad}–${bd} ${monthB} ${by}`;
  if (ay === by) return `${ad} ${monthA} – ${bd} ${monthB} ${by}`;
  return `${ad} ${monthA} ${ay} – ${bd} ${monthB} ${by}`;
}

/** "09:00–10:00" for hour 9 of the organisation day. */
export function formatHourRange(hour: number): string {
  return `${pad(hour)}:00–${pad((hour + 1) % 24)}:00`;
}
